import { Box, Icon, IconButton, SliderModal, Stack, Text, VStack } from '@genial/components';
import { BoxBorder, BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { useBreakpointValue, Utils } from '@genial/utils';
import { Pressable } from 'native-base';
import React, { useEffect } from 'react';
import { Dimensions, Pressable as RNPressable, StyleSheet, View } from 'react-native';

const getModalMaxHeight = () => Dimensions.get('window').height * 0.92;

interface RequestModalShellProps {
  sliderWidth: number;
  modalInsideModal: boolean;
  onDismiss: () => void;
  selectedCategoryLabel: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  headerDismissIcon?: 'back' | 'close';
  headerPaddingTop?: string | number;
  headerPaddingHorizontal?: string | number;
  headerPaddingBottom?: string | number;
  hideCategoryLabel?: boolean;
  hideHeader?: boolean;
}

type RequestModalLayout = 'mobile' | 'tablet' | 'desktop';

const tabletStyles = StyleSheet.create({
  root: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
  },
  card: {
    width: '90%',
    zIndex: 11,
    elevation: 4,
    shadowColor: 'rgba(54, 77, 205, 0.15)',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 1,
    shadowRadius: 48,
  },
});

const HeaderCloseButton: React.FC<{ onPress: () => void; testId: string }> = ({
  onPress,
  testId,
}) => (
  <IconButton
    testId={testId}
    icon="CloseModal"
    iconSize="xs"
    borderWidth="1"
    variant="rounded"
    backgroundColor="transparent"
    iconColor={Color['brand.primary.principal']}
    borderColor={Color['brand.primary.principal']}
    onPress={onPress}
    p={0}
  />
);

export const RequestModalShell: React.FC<RequestModalShellProps> = ({
  sliderWidth,
  modalInsideModal,
  onDismiss,
  selectedCategoryLabel,
  children,
  footer,
  headerDismissIcon = 'back',
  headerPaddingTop,
  headerPaddingHorizontal,
  headerPaddingBottom,
  hideCategoryLabel = false,
  hideHeader = false,
}) => {
  const layout = useBreakpointValue<RequestModalLayout>({
    base: 'mobile',
    sm: 'tablet',
    lg: 'desktop',
  });

  const showClose = headerDismissIcon === 'close';
  const modalMaxHeight = getModalMaxHeight();
  const resolvedHeaderPaddingTop = headerPaddingTop ?? Spacing['inline.xxs'];
  const resolvedHeaderPaddingHorizontal = headerPaddingHorizontal ?? Spacing['inline.xxs'];
  const resolvedHeaderPaddingBottom = headerPaddingBottom ?? Spacing['inline.nano'];

  useEffect(() => {
    if (layout !== 'tablet' || modalInsideModal) {
      return;
    }

    const htmlElement = document.querySelector('html');
    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = htmlElement?.style.overflow;

    document.body.style.overflow = 'hidden';
    if (htmlElement) {
      htmlElement.style.overflow = 'hidden';
    }

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      if (htmlElement) {
        htmlElement.style.overflow = previousHtmlOverflow ?? '';
      }
    };
  }, [layout, modalInsideModal]);

  const title = (
    <Text
      variant="bold"
      fontSize={Typography['fontsize.lg']}
      lineHeight="distant"
      color="neutral.black"
      flex={1}>
      {selectedCategoryLabel}
    </Text>
  );

  const footerBlock = footer ? (
    <Stack
      bg="neutral.white"
      alignItems="stretch"
      p={Spacing['inline.xxs']}
      borderTopWidth={1}
      w="full"
      borderTopColor="neutral.lightest"
      borderBottomRadius={layout === 'tablet' ? BoxBorder.heavy : undefined}>
      {footer}
    </Stack>
  ) : null;

  if (layout === 'tablet') {
    return (
      <View style={tabletStyles.root} testID="report-generator-request-modal">
        <RNPressable
          style={[
            tabletStyles.backdrop,
            { backgroundColor: Utils.ColorToken.WithOpacity('neutral.light', 0.5) },
          ]}
          onPress={onDismiss}
          accessibilityRole="button"
          accessibilityLabel="Fechar"
        />
        <View style={[tabletStyles.card, { maxHeight: modalMaxHeight }]}>
          <Box
            w="full"
            maxH={modalMaxHeight}
            bg={Color['neutral.white']}
            borderRadius={BoxBorder.heavy}
            overflow="hidden">
            <VStack
              w="full"
              maxH={modalMaxHeight}
              flexShrink={1}
              bg={Color['neutral.white']}
              borderRadius={BoxBorder.heavy}>
              {!hideHeader ? (
                <Stack
                  direction="row"
                  px={resolvedHeaderPaddingHorizontal}
                  pt={resolvedHeaderPaddingTop}
                  pb={resolvedHeaderPaddingBottom}
                  alignItems="center"
                  justifyContent={hideCategoryLabel ? 'flex-end' : 'space-between'}
                  space={Spacing['stack.quarck']}
                  flexShrink={0}>
                  {!hideCategoryLabel ? title : null}
                  <HeaderCloseButton
                    testId="report-generator-request-modal-close"
                    onPress={onDismiss}
                  />
                </Stack>
              ) : null}

              <Box w="full" flex={1} minH={0}>
                {children}
              </Box>
              {footerBlock}
            </VStack>
          </Box>
        </View>
      </View>
    );
  }

  const directionIsBottom = layout === 'mobile';

  return (
    <SliderModal
      dismissWhenClickOutside
      onDismiss={onDismiss}
      direction={directionIsBottom ? 'bottom' : 'right'}
      width={sliderWidth}
      modalInsideModal={modalInsideModal}>
      <VStack
        w="full"
        bg={Color['neutral.white']}
        borderTopRadius={{ base: Spacing['inline.xxxs'], lg: 0 }}
        flex={directionIsBottom ? undefined : 1}
        maxH={directionIsBottom ? modalMaxHeight : undefined}
        flexShrink={directionIsBottom ? 1 : undefined}>
        {!hideHeader ? (
          <VStack
            w="full"
            pt={headerPaddingTop !== undefined ? resolvedHeaderPaddingTop : Spacing['inline.xxxs']}
            pb={hideCategoryLabel ? 0 : Spacing['inline.xxxs']}
            bg={Color['neutral.white']}
            space={hideCategoryLabel ? 0 : Spacing['inline.xxxs']}>
            {directionIsBottom ? (
              <Box
                w="55px"
                alignSelf="center"
                background="neutral.lightest"
                borderRadius={BoxRadius.sm}
                h={1}
              />
            ) : null}
            <Stack
              direction="row"
              px={resolvedHeaderPaddingHorizontal}
              alignItems="center"
              justifyContent={
                hideCategoryLabel ? 'flex-end' : showClose ? 'space-between' : 'flex-start'
              }
              space={Spacing['stack.quarck']}>
              {showClose ? (
                <>
                  {!hideCategoryLabel ? title : null}
                  <HeaderCloseButton
                    testId="report-generator-request-modal-close"
                    onPress={onDismiss}
                  />
                </>
              ) : (
                <>
                  <Pressable
                    testID="report-generator-request-modal-back"
                    onPress={onDismiss}
                    accessibilityRole="button"
                    accessibilityLabel="Voltar">
                    <Icon
                      name="ArrowLeft"
                      color={Color['neutral.black']}
                      size="xs"
                      testId="report-generator-request-back-icon"
                    />
                  </Pressable>
                  {title}
                </>
              )}
            </Stack>
          </VStack>
        ) : directionIsBottom ? (
          <VStack w="full" pt={Spacing['inline.xxxs']} pb={0} bg={Color['neutral.white']}>
            <Box
              w="55px"
              alignSelf="center"
              background="neutral.lightest"
              borderRadius={BoxRadius.sm}
              h={1}
            />
          </VStack>
        ) : null}

        <Box w="full" flex={1} minH={0} bg={Color['neutral.white']}>
          {children}
        </Box>
        {footerBlock}
      </VStack>
    </SliderModal>
  );
};
